import { useEffect, useLayoutEffect, useRef } from 'react'
import { Socket, io } from 'socket.io-client'
import { useAuth } from './hooks_ext'
import { trigger } from './trigger'
import { dev } from './util'
import { createSocketSession, socketRooms, SocketAuth } from './socket-lifecycle'

const ENDPOINT = dev ? window.origin.replace(/(:\d+)?$/, ':5050') : window.origin

export const socket = trigger.value<Socket>()
const connection = trigger.value<{ local: Socket }>()

// One root owns the transport and serial authentication; callers own listeners
// and room leases. Keep the same socket object across ordinary reconnects.
export const useConnectToSocketIo = () => {
    const auth = useAuth()
    const latest = useRef<SocketAuth>({ user: auth.user, token: auth.token })
    const session = useRef<ReturnType<typeof createSocketSession>>(undefined)
    useLayoutEffect(() => {
        latest.current = { user: auth.user, token: auth.token }
        session.current?.updateAuth(latest.current)
    }, [auth.user, auth.token])
    useEffect(() => {
        const local = io(ENDPOINT, { transports: ['websocket'] })
        const owner = createSocketSession(local, latest.current, () => {
            socket.set(local)
            connection.set({ local })
        })
        session.current = owner
        return () => {
            owner.dispose()
            session.current = undefined
            if (socket.get() === local) socket.set(undefined)
            if (connection.get()?.local === local) connection.set(undefined)
            local.disconnect()
        }
    }, [])
}

type Handlers = { [event: string]: (...args: any[]) => any }
type Options = { room?: string, on?: Handlers, connect?: (socket: Socket) => any }

function useCommitted<T>(value: T) {
    const ref = useRef(value)
    useLayoutEffect(() => { ref.current = value })
    return ref
}

function useSocketEvents(local: Socket, handlers?: Handlers, prefix = '') {
    const current = useCommitted(handlers)
    const events = JSON.stringify(Object.keys(handlers || {}).sort())
    useEffect(() => {
        if (!local) return
        const listeners = (JSON.parse(events) as string[]).map(event => {
            const callback = (...args: any[]) => current.current?.[event]?.apply(local, args)
            local.on(prefix + event, callback)
            return () => local.off(prefix + event, callback)
        })
        return () => { listeners.forEach(remove => remove()) }
    }, [local, events, prefix, current])
}

function useConnectionCallback(local: Socket, room: string, callback?: (socket: Socket) => any) {
    const current = useCommitted(callback)
    const [ready] = connection.use()
    const hasCallback = !!callback
    useEffect(() => {
        if (local && ready?.local === local && socketRooms(local).isReady() && hasCallback) current.current?.(local)
    }, [local, ready, room, hasCallback, current])
}

function useOwnedSocket(protocol: 'named' | 'generic', { room = '', on, connect }: Options, ins?: Handlers) {
    const [local] = socket.use()
    // Install listeners before joining or requesting initial data.
    useSocketEvents(local, on)
    useSocketEvents(local, ins, room + ':')
    useEffect(() => {
        if (local && room) return socketRooms(local).acquire(protocol, room)
    }, [local, room, protocol])
    useConnectionCallback(local, room, connect)
    return local
}

export const useUserSocket = (roomToJoin = '', ons?: Handlers, emits?: (socket: Socket) => any) =>
    useOwnedSocket('named', { room: roomToJoin, on: ons, connect: emits })

export const useSocket = (options: Options = {}) => useOwnedSocket('named', options)

export const useRoom = ({ in: ins, ...options }: Options & { in?: Handlers } = {}) =>
    useOwnedSocket('generic', options, ins)
